import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Car, Star, Plus } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { VehicleCard } from './VehicleCard';
import { VehicleFormDialog } from './VehicleFormDialog';

interface Vehicle {
  id: string;
  user_id: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  nickname: string;
  modifications: string;
  photo_urls: any;
  primary_photo_url: string;
  is_featured: boolean;
}

interface ProfileVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  nickname?: string;
  modifications?: string;
  primary_photo_url?: string;
  is_featured: boolean;
}

interface ProfileGarageProps {
  vehicles?: Vehicle[];
  isLoading?: boolean;
  isOwnProfile?: boolean;
  userId?: string;
  onUpdate?: (data: any) => void;
  onDelete?: (id: string) => void;
  onSetFeatured?: (id: string) => void;
  onCreate?: (data: any) => void;
}

export const ProfileGarage = ({ 
  vehicles, 
  isLoading,
  isOwnProfile = false,
  userId,
  onUpdate,
  onDelete,
  onSetFeatured,
  onCreate,
}: ProfileGarageProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            Garage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Garage
            </CardTitle>
            {isOwnProfile && onCreate && (
              <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Car className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
            <p className="text-muted-foreground mb-4">No vehicles in garage yet</p>
            {isOwnProfile && onCreate && (
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Vehicle
              </Button>
            )}
          </div>
        </CardContent>
        
        {isOwnProfile && onCreate && userId && (
          <VehicleFormDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onSubmit={(data) => {
              onCreate(data);
              setIsAddDialogOpen(false);
            }}
            userId={userId}
          />
        )}
      </Card>
    );
  }

  // If own profile with edit capabilities, show editable cards
  if (isOwnProfile && onUpdate && onDelete && onSetFeatured) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                Garage ({vehicles.length})
              </CardTitle>
              {onCreate && (
                <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onSetFeatured={onSetFeatured}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {onCreate && userId && (
          <VehicleFormDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onSubmit={(data) => {
              onCreate(data);
              setIsAddDialogOpen(false);
            }}
            userId={userId}
          />
        )}
      </>
    );
  }

  // View-only mode for other profiles
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          Garage ({vehicles.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((vehicle) => (
            <div
              key={vehicle.id}
              className="group relative overflow-hidden rounded-lg border bg-card hover:shadow-lg transition-shadow"
            >
              {vehicle.primary_photo_url ? (
                <AspectRatio ratio={16 / 9}>
                  <img
                    src={vehicle.primary_photo_url}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="object-cover w-full h-full photo-signature"
                  />
                </AspectRatio>
              ) : (
                <AspectRatio ratio={16 / 9}>
                  <div className="bg-muted flex items-center justify-center h-full">
                    <Car className="h-12 w-12 text-muted-foreground opacity-50" />
                  </div>
                </AspectRatio>
              )}
              
              {vehicle.is_featured && (
                <Badge className="absolute top-2 right-2 bg-amber-500">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}

              <div className="p-4">
                <h3 className="font-semibold text-foreground mb-1">
                  {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                </h3>
                {vehicle.nickname && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                    {vehicle.trim && ` ${vehicle.trim}`}
                  </p>
                )}
                {vehicle.modifications && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {vehicle.modifications}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
